import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  ArrangeLevel,
  Arrangement,
  ArrangementResource,
  ArrangementType,
  Cluster,
  ContentItem,
  CreateArrangementInterface,
  CreateArrangementResponseInterface,
  Method,
  MethodDetail,
  Resource,
  ResourceInput,
  ResourceType,
  SearchResult,
  UpdateArrangementInterface,
  VdexNode,
} from '@thiememeulenhoff/types';
import { combineLatest, map } from 'rxjs';
import { sanitizeResourcesOperator } from './_util/sanitizeResources';
import { FilterToParamsHelper } from '../helpers/filterToParams.helper';
import { ArrangementFilterParamsMap } from './const/arrangementFilterParams';
import { ContentEndpointsService } from './content.endpoints.service';
import { ArrangementFilter } from './types/arrangement.filter';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  readonly #http = inject(HttpClient);

  readonly #endpoints = inject(ContentEndpointsService);

  public fetchPublishedArrangementsOfType(type: ArrangementType) {
    return this.#fetchArrangementsByFilter({
      userDelete: false,
      includeCreatedByActor: true,
      withPublications: true,
      type,
    });
  }

  public fetchUserDefinedArrangements(methodSlug: string) {
    return this.#fetchArrangementsByFilter({
      arrangeable: true,
      type: 'userdefined',
      methodSlug,
      userDelete: false,
    });
  }

  public fetchPredefinedArrangements(methodSlug: string) {
    return this.#fetchArrangementsByFilter({
      arrangeable: true,
      type: 'predefined',
      methodSlug,
      userDelete: false,
    });
  }

  public fetchTemplateArrangements(methodSlug: string) {
    const editorialTemplates$ = this.#fetchArrangementsByFilter({
      arrangeable: true,
      withPublications: true,
      type: 'editorial',
      methodSlug,
      userDelete: false,
    });

    const predefinedTemplates$ = this.#fetchArrangementsByFilter({
      arrangeable: true,
      type: 'predefined',
      methodSlug,
      userDelete: false,
    });

    return combineLatest([editorialTemplates$, predefinedTemplates$]).pipe(
      map(([editorialTemplates, predefinedTemplates]) => [
        ...editorialTemplates,
        ...predefinedTemplates,
      ]),
    );
  }

  #fetchArrangementsByFilter(filterInput: Partial<ArrangementFilter> = {}) {
    const endpoint = this.#endpoints.arrangements();
    const params = FilterToParamsHelper(
      filterInput,
      ArrangementFilterParamsMap,
    );
    return this.#http.get<Array<Arrangement>>(endpoint, { params });
  }

  public fetchClusters() {
    const endpoint = this.#endpoints.clusters();
    return this.#http.get<Array<Cluster>>(endpoint);
  }

  public fetchMethods() {
    const endpoint = this.#endpoints.methods();
    return this.#http.get<Array<Method>>(endpoint);
  }

  public fetchMethodBySlug = (methodSlug: string) => {
    const endpoint = this.#endpoints.methodBySlug(methodSlug);
    return this.#http.get<MethodDetail>(endpoint);
  };

  public fetchClusterByMethodSlug = (methodSlug: string) => {
    const endpoint = this.#endpoints.clusterByMethodSlug(methodSlug);
    return this.#http.get<Array<Cluster>>(endpoint);
  };

  public fetchArrangementByCode = (code: string) => {
    const endpoint = this.#endpoints.arrangementByCode(code);
    const params = FilterToParamsHelper(
      { userDelete: false },
      ArrangementFilterParamsMap,
    );
    return this.#http.get<Arrangement>(endpoint, { params });
  };

  public fetchResourceByArrangementCode = <
    T extends ArrangementResource = ArrangementResource | Resource,
  >(
    arrangementCode: string,
    contentIdentifier = '',
    depth = 1,
    fields:
      | { include?: Array<string>; exclude?: Array<string> }
      | undefined = undefined,
    types:
      | { include?: Array<ResourceType>; exclude?: Array<ResourceType> }
      | undefined = undefined,
    disabled = false,
  ) => {
    const endpoint =
      this.#endpoints.resourcesByArrangementCode(arrangementCode);
    const params = new HttpParams({
      fromObject: {
        depth,
        html: true,
        ...(disabled ? { disabled } : {}),
        ...(contentIdentifier === '' ? {} : { contentIdentifier }),
        ...(fields
          ? { fields: this.#formatIncludeExcludeIntoString(fields) }
          : {}),
        ...(types
          ? { resourceTypes: this.#formatIncludeExcludeIntoString(types) }
          : {}),
      },
    });

    return this.#http
      .get<Array<T>>(endpoint, { params })
      .pipe(sanitizeResourcesOperator<T>());
  };

  public fetchContentById = (
    streamCode: string,
    contentIdentifier: string,
    includeSourceMaterial = false,
  ) => {
    const endpoint = this.#endpoints.contentById(streamCode, contentIdentifier);

    const params = {
      ...(includeSourceMaterial ? { includeSourceMaterial } : {}),
    };

    return this.#http.get(endpoint, {
      responseType: 'text',
      params,
    });
  };

  public fetchRawContentById = (
    streamCode: string,
    contentIdentifier: string,
    includeSourceMaterial = false,
  ) => {
    const endpoint = this.#endpoints.rawContentById(
      streamCode,
      contentIdentifier,
    );

    const params = {
      ...(includeSourceMaterial ? { includeSourceMaterial } : {}),
    };

    return this.#http.get(endpoint, {
      responseType: 'text',
      params,
    });
  };

  public search = (
    query: string,
    arrangementCode: string | undefined,
    methodSlug: string | undefined,
    facetTypes: Array<string>,
    resultFacets: Array<string>,
    scrollId: string | undefined,
  ) => {
    const endpoint = this.#endpoints.search();

    const scope = {
      ...(arrangementCode ? { arrangementCode } : {}),
      ...(methodSlug
        ? { methodCode: methodSlug, arrangementType: 'editorial' }
        : {}),
    };

    const facets = {
      digitalResourceTypes: facetTypes,
    };

    const results = {
      digitalResourceTypes: resultFacets,
      size: 20,
      scrollId,
    };

    const body = {
      query,
      scope,
      facets,
      results,
    };

    return this.#http.post<SearchResult>(endpoint, body);
  };

  public fetchMultipleResources = (
    arrangementContentIdentifiers: Array<{
      arrangementCode: string;
      contentIdentifier: string;
    }>,
    fields: { include?: Array<string>; exclude?: Array<string> } = {},
    types:
      | { include?: Array<ResourceType>; exclude?: Array<ResourceType> }
      | undefined = undefined,
  ) => {
    const params = new HttpParams({
      fromObject: {
        html: true,
        'arrangementContentIdentifiers[]': arrangementContentIdentifiers.map(
          ({ arrangementCode, contentIdentifier }) =>
            `${arrangementCode}:${contentIdentifier}`,
        ),
        ...(fields
          ? { fields: this.#formatIncludeExcludeIntoString(fields) }
          : {}),
        ...(types
          ? { resourceTypes: this.#formatIncludeExcludeIntoString(types) }
          : {}),
      },
    });

    return this.#http
      .get<Array<Resource>>(this.#endpoints.multipleResources(), { params })
      .pipe(sanitizeResourcesOperator());
  };

  updateArrangement(
    arrangement: UpdateArrangementInterface,
    arrangementCode: string,
  ) {
    return this.#http.put<Arrangement>(
      this.#endpoints.arrangementByCode(arrangementCode),
      arrangement,
    );
  }

  saveArrangementContent(
    arrangementCode: string,
    content: Array<ResourceInput>,
  ) {
    return this.#http.post<Array<Resource>>(
      this.#endpoints.saveArrangement(arrangementCode),
      content,
    );
  }

  createArrangement(data: CreateArrangementInterface) {
    return this.#http.post<CreateArrangementResponseInterface>(
      this.#endpoints.createArrangement(),
      data,
    );
  }

  fetchVdexDefinition(arrangementCode: string, apiField: string) {
    return this.#http.get<Array<VdexNode>>(
      this.#endpoints.vdexDefinitionByApiField(arrangementCode, apiField),
    );
  }

  public fetchAvailableLearningItems(
    methodSlug: string,
    resourceType: ArrangeLevel,
  ) {
    const endpoint = this.#endpoints.resourcesByMethodSlug(
      methodSlug,
      resourceType,
    );

    const params = {
      arrangeable: true,
      mergeDescendantMetadata: true,
      mergeArrangementMetadata: true,
      withChildren: true,
    };

    return this.#http
      .get<Array<Resource>>(endpoint, { params })
      .pipe(sanitizeResourcesOperator());
  }

  // eslint-disable-next-line class-methods-use-this
  #formatIncludeExcludeIntoString(values: {
    include?: Array<string>;
    exclude?: Array<string>;
  }): string {
    const excludeValues = values.exclude?.map((value) => `-${value}`) ?? [];

    return [...(values.include ?? []), ...excludeValues].join(',');
  }

  fetchByContentPath = (
    streamCode: string,
    contentPath: string,
    depth?: number,
    digitalResourceTypes?: Array<string>,
  ) => {
    const endpoint = this.#endpoints.contentByPath(streamCode, contentPath);

    const params = new HttpParams({
      fromObject: {
        ...(typeof depth === 'number' ? { depth } : {}),
        ...(digitalResourceTypes
          ? { digitalResourceTypes: digitalResourceTypes.join(',') }
          : {}),
      },
    });

    return this.#http.get<Array<ContentItem>>(endpoint, { params });
  };
}
